const STORAGE_KEY = "hydrotools:client-project-context:v1";

const BOOTSTRAP = {
  clients: [
    { id: "safeharbor", name: "Safe Harbor Marinas", slug: "safeharbor", createdAt: "2026-01-01T00:00:00.000Z" },
  ],
  projects: [
    {
      id: "llm", clientId: "safeharbor", name: "LLM Marina", slug: "llm",
      horizontalCRS: "EPSG:2227", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "LLM",
      s3Prefix: "clients/safeharbor/llm",
      location: { lat: 37.8715, lng: -122.3375, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
    {
      id: "htm", clientId: "safeharbor", name: "HTM Marina", slug: "htm",
      horizontalCRS: "EPSG:2236", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "HTM",
      s3Prefix: "clients/safeharbor/htm",
      location: { lat: 24.5611, lng: -81.7998, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
    {
      id: "rsbc", clientId: "safeharbor", name: "RSBC Marina", slug: "rsbc",
      horizontalCRS: "EPSG:2236", horizontalUnits: "ftUS",
      verticalDatum: "MLLW", verticalUnits: "ft",
      geoidModel: "GEOID18", marinaId: "RSBC",
      s3Prefix: "clients/safeharbor/rsbc",
      location: { lat: 25.7745, lng: -80.1300, zoom: 16 },
      createdAt: "2026-01-01T00:00:00.000Z",
    },
  ],
  activeClientId: "safeharbor",
  activeProjectId: null,
};

function slugify(value, fallback = "project") {
  const slug = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallback;
}

function titleFromSlug(value) {
  return String(value || "")
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || String(value || "Project");
}

function clientFromPrefix(prefix) {
  const parts = String(prefix || "").split("/").filter(Boolean);
  if (parts[0] === "clients" && parts[1]) return slugify(parts[1], "client");
  return "safeharbor";
}

function normalizeClient(client) {
  const slug = slugify(client?.slug || client?.id || client?.name, "client");
  return {
    id: slug,
    slug,
    name: String(client?.name || titleFromSlug(slug)),
    createdAt: String(client?.createdAt || ""),
  };
}

function normalizeProject(project) {
  const settings = project?.settings && typeof project.settings === "object" ? project.settings : {};
  const s3Prefix = String(project?.s3Prefix || "").replace(/^\/+|\/+$/g, "");
  const clientId = slugify(project?.clientId || project?.clientSlug || clientFromPrefix(s3Prefix), "safeharbor");
  const slug = slugify(project?.slug || project?.projectSlug || project?.code || project?.id || project?.name, "project");
  const marinaId = String(project?.marinaId || project?.code || slug.toUpperCase()).trim().toUpperCase();
  const lat = Number(project?.location?.lat ?? project?.lat);
  const lng = Number(project?.location?.lng ?? project?.lng);
  return {
    id: String(project?.id || marinaId || `${clientId}-${slug}`),
    clientId,
    clientSlug: clientId,
    clientName: String(project?.clientName || titleFromSlug(clientId)),
    name: String(project?.name || titleFromSlug(slug)),
    slug,
    code: String(project?.code || marinaId),
    horizontalCRS: String(project?.horizontalCRS || settings.horizontalCRS || settings.crs || "EPSG:4326"),
    horizontalUnits: String(project?.horizontalUnits || settings.horizontalUnits || settings.linearUnits || "m"),
    verticalDatum: String(project?.verticalDatum || settings.verticalDatum || "MSL"),
    verticalUnits: String(project?.verticalUnits || settings.verticalUnits || "m"),
    geoidModel: String(project?.geoidModel || settings.geoidModel || "None"),
    marinaId,
    s3Prefix: s3Prefix || `clients/${clientId}/${slug}`,
    location: Number.isFinite(lat) && Number.isFinite(lng)
      ? { lat, lng, zoom: Number(project?.location?.zoom || 16) }
      : null,
    type: String(project?.type || project?.projectType || "marina"),
    active: project?.active !== false,
    createdAt: String(project?.createdAt || ""),
    updatedAt: String(project?.updatedAt || ""),
  };
}

function dedupeById(items) {
  const byId = new Map();
  for (const item of items) byId.set(item.id, { ...(byId.get(item.id) || {}), ...item });
  return [...byId.values()];
}

async function registerProjectWithServer(project, client) {
  if (!project?.marinaId || !client?.slug || !project?.slug) return null;
  const resp = await fetch("/api/marina/projects/register", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      marinaId: project.marinaId,
      clientSlug: client.slug,
      clientName: client.name,
      projectSlug: project.slug,
      name: project.name,
      horizontalCRS: project.horizontalCRS,
      horizontalUnits: project.horizontalUnits,
      verticalDatum: project.verticalDatum,
      verticalUnits: project.verticalUnits,
      geoidModel: project.geoidModel,
      location: project.location,
    }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
  return payload?.project ? normalizeProject(payload.project) : project;
}

export function createContextService({ events, projections }) {
  let state = load();
  let refreshPromise = null;

  function load() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (parsed && Array.isArray(parsed?.clients) && parsed.clients.length > 0) {
        return {
          clients: dedupeById(parsed.clients.map(normalizeClient)),
          projects: dedupeById((Array.isArray(parsed?.projects) ? parsed.projects : []).map(normalizeProject)),
          activeClientId: parsed?.activeClientId || null,
          activeProjectId: parsed?.activeProjectId || null,
          catalogStatus: "cached",
          catalogError: "",
        };
      }
    } catch {}
    return {
      clients: BOOTSTRAP.clients.map(normalizeClient),
      projects: BOOTSTRAP.projects.map(normalizeProject),
      activeClientId: BOOTSTRAP.activeClientId,
      activeProjectId: BOOTSTRAP.activeProjectId,
      catalogStatus: "bootstrap",
      catalogError: "",
    };
  }

  function persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      clients: state.clients,
      projects: state.projects,
      activeClientId: state.activeClientId,
      activeProjectId: state.activeProjectId,
    }));
  }

  function applyCRS(project) {
    if (!project || !projections) return;
    projections.setProjectProfile({
      name: project.name,
      horizontalCRS: project.horizontalCRS || "EPSG:4326",
      verticalDatum: project.verticalDatum || "MSL",
      geoidModel: project.geoidModel || "None",
      linearUnits: project.horizontalUnits || "meters",
      angularUnits: "degrees",
    });
  }

  function mergeCatalog(clients, projects) {
    const normalizedProjects = projects.map(normalizeProject);
    const generatedClients = normalizedProjects.map((project) => normalizeClient({
      id: project.clientId,
      slug: project.clientId,
      name: project.clientName,
      createdAt: project.createdAt,
    }));
    state.clients = dedupeById([...state.clients, ...clients.map(normalizeClient), ...generatedClients])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    state.projects = dedupeById([...state.projects, ...normalizedProjects])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    if (state.activeProjectId && !state.projects.some((p) => p.id === state.activeProjectId)) {
      state.activeProjectId = null;
    }
    if (state.activeClientId && !state.clients.some((c) => c.id === state.activeClientId)) {
      state.activeClientId = state.projects.find((p) => p.id === state.activeProjectId)?.clientId || state.clients[0]?.id || null;
    }
    persist();
  }

  async function refreshFromServer({ emit = true } = {}) {
    if (refreshPromise) return refreshPromise;
    state.catalogStatus = "loading";
    refreshPromise = (async () => {
      try {
        const resp = await fetch("/api/projects/catalog?scope=context", { headers: { Accept: "application/json" }, cache: "no-store" });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
        const catalog = payload?.catalog || {};
        // Replace catalog from S3 — source of truth, not additive merge
        const fetchedProjects = (Array.isArray(catalog.projects) ? catalog.projects : []).map(normalizeProject);
        const fetchedClients = (Array.isArray(catalog.clients) ? catalog.clients : []).map(normalizeClient);
        const derivedClients = fetchedProjects.map((p) => normalizeClient({ id: p.clientId, slug: p.clientId, name: p.clientName, createdAt: p.createdAt }));
        state.projects = dedupeById(fetchedProjects).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        state.clients = dedupeById([...fetchedClients, ...derivedClients]).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        if (state.activeProjectId && !state.projects.some((p) => p.id === state.activeProjectId)) state.activeProjectId = null;
        if (state.activeClientId && !state.clients.some((c) => c.id === state.activeClientId)) {
          state.activeClientId = state.projects.find((p) => p.id === state.activeProjectId)?.clientId || state.clients[0]?.id || null;
        }
        persist();
        state.catalogStatus = "synced";
        state.catalogError = "";
        applyCRS(getActiveProject());
        if (emit) events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
      } catch (err) {
        state.catalogStatus = "error";
        state.catalogError = String(err?.message || err);
        if (emit) events.emit("context:catalog:error", { error: state.catalogError });
      } finally {
        refreshPromise = null;
      }
      return { clients: getClients(), projects: getAllProjects(), status: state.catalogStatus, error: state.catalogError };
    })();
    return refreshPromise;
  }

  function getClients() { return [...state.clients]; }
  function getAllProjects() { return [...state.projects]; }
  function getProjects(clientId) { return state.projects.filter((p) => p.clientId === clientId); }
  function getCatalogStatus() { return { status: state.catalogStatus, error: state.catalogError }; }
  function getActiveClient() { return state.clients.find((c) => c.id === state.activeClientId) || null; }
  function getActiveProject() { return state.projects.find((p) => p.id === state.activeProjectId) || null; }

  function setActiveProject(projectId) {
    const project = state.projects.find((p) => p.id === projectId);
    if (!project) return;
    state.activeProjectId = projectId;
    state.activeClientId = project.clientId;
    persist();
    applyCRS(project);
    const client = getActiveClient();
    registerProjectWithServer(project, client).catch((err) => {
      state.catalogStatus = "error";
      state.catalogError = String(err?.message || err);
      events.emit("context:catalog:error", { error: state.catalogError });
    });
    events.emit("context:changed", { client, project });
    if (project.marinaId) {
      document.dispatchEvent(new CustomEvent("hydrotools:marina-changed", {
        detail: { marinaId: project.marinaId, fromContext: true, horizontalCRS: project.horizontalCRS || "" },
      }));
    }
  }

  async function createClient({ name }) {
    const slug = slugify(name, `client-${Date.now()}`);
    const draft = normalizeClient({ id: slug, name, slug, createdAt: new Date().toISOString() });
    const resp = await fetch("/api/clients", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ name: draft.name, clientSlug: draft.slug }),
    });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok || payload?.ok === false) throw new Error(payload?.error || `HTTP ${resp.status}`);
    const saved = payload.client ? normalizeClient(payload.client) : draft;
    state.clients = dedupeById([...state.clients, saved]);
    state.activeClientId = saved.id;
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    return saved;
  }

  async function createProject(data) {
    const { clientId, name, horizontalCRS, horizontalUnits, verticalDatum, verticalUnits, geoidModel, location } = data;
    const client = state.clients.find((c) => c.id === clientId) || null;
    if (!client) throw new Error("Select a client before creating a project.");
    const slug = slugify(name, `project-${Date.now()}`);
    const project = normalizeProject({
      id: slug.toUpperCase(),
      clientId,
      clientName: client.name,
      name,
      slug,
      code: slug.toUpperCase(),
      marinaId: slug.toUpperCase(),
      s3Prefix: `clients/${client.slug}/${slug}`,
      horizontalCRS: horizontalCRS || "EPSG:4326",
      horizontalUnits: horizontalUnits || "m",
      verticalDatum: verticalDatum || "MSL",
      verticalUnits: verticalUnits || "m",
      geoidModel: geoidModel || "None",
      location: location || null,
      createdAt: new Date().toISOString(),
    });
    const saved = await registerProjectWithServer(project, client);
    state.projects = dedupeById([...state.projects, saved]);
    state.activeClientId = saved.clientId;
    state.catalogStatus = "synced";
    state.catalogError = "";
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    return saved;
  }

  function updateProject(projectId, updates) {
    const idx = state.projects.findIndex((p) => p.id === projectId);
    if (idx < 0) return null;
    state.projects[idx] = normalizeProject({ ...state.projects[idx], ...updates });
    persist();
    const client = state.clients.find((c) => c.id === state.projects[idx].clientId) || null;
    registerProjectWithServer(state.projects[idx], client).catch((err) => {
      state.catalogStatus = "error";
      state.catalogError = String(err?.message || err);
      events.emit("context:catalog:error", { error: state.catalogError });
    });
    if (state.activeProjectId === projectId) {
      applyCRS(state.projects[idx]);
      events.emit("context:changed", { client: getActiveClient(), project: state.projects[idx] });
    }
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
    return state.projects[idx];
  }

  function deleteProject(projectId) {
    state.projects = state.projects.filter((p) => p.id !== projectId);
    if (state.activeProjectId === projectId) {
      state.activeProjectId = null;
      events.emit("context:changed", { client: getActiveClient(), project: null });
    }
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
  }

  function deleteClient(clientId) {
    state.clients = state.clients.filter((c) => c.id !== clientId);
    state.projects = state.projects.filter((p) => p.clientId !== clientId);
    if (state.activeClientId === clientId) {
      state.activeClientId = state.clients[0]?.id || null;
      state.activeProjectId = null;
      events.emit("context:changed", { client: null, project: null });
    }
    persist();
    events.emit("context:catalog:changed", { clients: getClients(), projects: getAllProjects() });
  }

  persist();
  applyCRS(getActiveProject());
  refreshFromServer({ emit: true });

  // Re-sync whenever the user returns to the tab or every 3 minutes
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") refreshFromServer({ emit: true });
  });
  setInterval(() => refreshFromServer({ emit: true }), 3 * 60 * 1000);

  return {
    getClients,
    getProjects,
    getAllProjects,
    getActiveClient,
    getActiveProject,
    getCatalogStatus,
    refreshFromServer,
    setActiveProject,
    createClient,
    createProject,
    updateProject,
    deleteProject,
    deleteClient,
  };
}
